import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Eye, GitCompare, Loader2, MinusCircle, PencilLine, PlusCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const FIELD_LABELS = {
  block_definition: "Block Definition",
  panel_type: "Panel Type",
  level: "Level",
  elevation: "Elevation",
  type_code: "Type Code",
  source_object_id: "Rhino Object ID",
};

export const COMPARE_COLORS = {
  added: "#059669",
  removed: "#DC2626",
  renamed: "#D97706",
  modified: "#0891B2",
  unchanged: "#CBD5E1",
};

const CATEGORIES = [
  { key: "added", label: "Added", icon: PlusCircle, tone: "text-emerald-700", border: "border-emerald-500/25" },
  { key: "removed", label: "Removed", icon: MinusCircle, tone: "text-red-700", border: "border-red-500/25" },
  { key: "renamed", label: "Renamed", icon: PencilLine, tone: "text-amber-700", border: "border-amber-500/25" },
  { key: "modified", label: "Modified", icon: GitCompare, tone: "text-cyan-700", border: "border-cyan-500/25" },
];

/** Builds panelUid -> colour for the viewer overlay from a compare result. */
export function buildCompareColorMap(result) {
  const map = new Map();
  if (!result) return map;
  CATEGORIES.forEach((category) => {
    (result[category.key] || []).forEach((row) => {
      const color = COMPARE_COLORS[category.key];
      [row.panel_uid, row.base_panel_uid].forEach((uid) => {
        if (uid) map.set(uid, color);
      });
    });
  });
  return map;
}

export function RevisionCompare({ projectId, revisions, onShowInViewer }) {
  const processed = revisions.filter((r) => r.processing_status === "processed");
  const [base, setBase] = useState(processed[1]?.id || "");
  const [target, setTarget] = useState(processed[0]?.id || "");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("added");

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.get(
        `/projects/${projectId}/models/compare?base=${base}&target=${target}`
      );
      setResult(data);
      const first = CATEGORIES.find((c) => data.summary[c.key] > 0);
      setTab(first?.key || "added");
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy(false);
    }
  };

  if (processed.length < 2)
    return (
      <p
        data-testid="compare-unavailable"
        className="rounded-lg border border-dashed border-slate-200 px-4 py-6 text-center text-xs text-slate-500"
      >
        At least two processed revisions are needed to compare.
      </p>
    );

  const rows = result?.[tab] || [];

  return (
    <div data-testid="revision-compare" className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[130px] flex-1">
          <p className="mono mb-1.5 text-[10px] uppercase tracking-wider text-slate-500">
            Base (older)
          </p>
          <Select value={base} onValueChange={setBase}>
            <SelectTrigger
              data-testid="compare-base-select"
              className="h-9 border-slate-200 bg-[#EEF2F8] text-xs"
            >
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent className="border-slate-200 bg-white text-slate-800">
              {processed.map((rev) => (
                <SelectItem key={rev.id} value={rev.id} className="mono text-xs">
                  {rev.revision_name} ({rev.panel_count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <ArrowRight size={15} className="mb-2.5 shrink-0 text-slate-500" />
        <div className="min-w-[130px] flex-1">
          <p className="mono mb-1.5 text-[10px] uppercase tracking-wider text-slate-500">
            Target (newer)
          </p>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger
              data-testid="compare-target-select"
              className="h-9 border-slate-200 bg-[#EEF2F8] text-xs"
            >
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent className="border-slate-200 bg-white text-slate-800">
              {processed.map((rev) => (
                <SelectItem key={rev.id} value={rev.id} className="mono text-xs">
                  {rev.revision_name} ({rev.panel_count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          data-testid="run-compare-button"
          onClick={run}
          disabled={!base || !target || base === target || busy}
          className="h-9 rounded-full bg-cyan-500 px-4 text-[11px] uppercase tracking-wider text-[#04121a] hover:bg-cyan-400"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : "Compare"}
        </Button>
      </div>

      {result ? (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5" data-testid="compare-summary">
            {[...CATEGORIES, { key: "unchanged", label: "Unchanged", tone: "text-slate-700", border: "border-slate-200" }].map(
              (category) => (
                <div
                  key={category.key}
                  data-testid={`compare-count-${category.key}`}
                  className={`rounded-lg border bg-[#EEF2F8] px-3 py-2.5 ${category.border}`}
                >
                  <p className={`mono text-xl ${category.tone}`}>
                    {result.summary[category.key]}
                  </p>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">
                    {category.label}
                  </p>
                </div>
              )
            )}
          </div>

          <div className="flex flex-wrap gap-1.5">
            {CATEGORIES.map((category) => (
              <button
                key={category.key}
                data-testid={`compare-tab-${category.key}`}
                onClick={() => setTab(category.key)}
                className={`mono rounded-full px-3 py-1.5 text-[10px] uppercase tracking-wider ${
                  tab === category.key
                    ? "bg-cyan-500/18 text-cyan-700"
                    : "border border-slate-200 text-slate-600 hover:bg-slate-100"
                }`}
              >
                {category.label} ({result.summary[category.key]})
              </button>
            ))}
          </div>

          <div
            data-testid="compare-results"
            className="okn-scroll max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-[#EEF2F8]"
          >
            {rows.length === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-slate-500">
                No panels in this category.
              </p>
            ) : (
              rows.map((row, index) => (
                <div
                  key={`${tab}-${index}`}
                  className="border-b border-slate-200 px-3 py-2 last:border-0"
                >
                  {tab === "renamed" ? (
                    <p className="mono flex flex-wrap items-center gap-1.5 text-[11px]">
                      <span className="text-red-700 line-through">{row.from_name}</span>
                      <ArrowRight size={11} className="text-slate-500" />
                      <span className="text-emerald-700">{row.to_name}</span>
                    </p>
                  ) : (
                    <p className="mono text-[11px] text-slate-800">{row.panel_name}</p>
                  )}
                  {row.changes?.length ? (
                    <p className="mt-1 text-[10px] text-slate-500">
                      {row.changes
                        .map(
                          (change) =>
                            `${FIELD_LABELS[change.field] || change.field}: ${
                              change.from ?? "—"
                            } → ${change.to ?? "—"}`
                        )
                        .join(" · ")}
                    </p>
                  ) : null}
                  {tab !== "renamed" && (row.level || row.block_definition) ? (
                    <p className="mono mt-0.5 text-[10px] text-slate-500">
                      {row.level} · {row.elevation} · {row.block_definition}
                    </p>
                  ) : null}
                </div>
              ))
            )}
          </div>
          <p className="text-[10px] text-slate-500">
            Renames are detected from the Rhino object GUID, so workflow history follows
            the panel even when its name changes.
          </p>
          {onShowInViewer ? (
            <Button
              data-testid="show-compare-in-viewer"
              onClick={() => onShowInViewer(result)}
              className="h-9 w-full rounded-full bg-slate-900 text-[11px] uppercase tracking-wider text-white hover:bg-slate-800"
            >
              <Eye size={14} className="mr-1.5" /> Show colour-coded in 3D model
            </Button>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
